import Link from 'next/link'
import { useSearchParams } from 'react-router-dom'
import { Plus, Building2, User, Archive, ArrowRight } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ShowArchivedToggle } from '@/app/(dashboard)/customers/_components/ShowArchivedToggle'
import { apiClient } from '@/lib/api/client'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'

export function CustomerListPage() {
  const [searchParams] = useSearchParams()
  const showArchived = searchParams.get('showArchived') === 'true'
  const { data, error, loading, reload } = useAsyncData(async () => {
    const [customers, profile] = await Promise.all([
      apiClient.listCustomers(showArchived),
      apiClient.getProfile().catch(() => null),
    ])

    return { customers, profile }
  }, [showArchived])

  if (loading) return <LoadingState label="Loading customers..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const customers = data?.customers ?? []
  const canManageCustomers = data?.profile?.is_admin || data?.profile?.role === 'admin'

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Customers"
        description="Manage customer accounts and portal access"
        breadcrumbs={[{ label: 'Customers' }]}
        actions={canManageCustomers ? (
          <Link href="/customers/new">
            <Button size="sm" className="h-8">
              <Plus className="mr-1 h-4 w-4" />
              Add Customer
            </Button>
          </Link>
        ) : null}
      />

      <div className="flex-1 space-y-4 overflow-auto p-4 md:p-6">
        <div className="flex items-center justify-between">
          <ShowArchivedToggle showArchived={showArchived} />
          <p className="text-sm text-muted-foreground">
            {customers.length} customer{customers.length === 1 ? '' : 's'}
          </p>
        </div>

        {customers.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">
                {showArchived
                  ? 'No customers found.'
                  : 'No active customers yet. Add your first customer to get started.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>TIN</TableHead>
                  {showArchived && <TableHead>Status</TableHead>}
                  <TableHead className="w-[96px] text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => {
                  const isArchived = !!customer.deleted_at
                  const customerHref = `/customers/${customer.id}`
                  const customerName = customer.company_name || customer.name

                  return (
                    <TableRow
                      key={customer.id}
                      className={`hover:bg-muted/50 ${isArchived ? 'opacity-60' : ''}`}
                    >
                      <TableCell>
                        <Link href={customerHref} className="block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <div className="flex items-center gap-2 font-medium">
                            {customerName}
                            {isArchived && <Archive className="h-3 w-3 text-muted-foreground" />}
                          </div>
                          {customer.company_name && (
                            <div className="text-xs text-muted-foreground">
                              {customer.contact_person || customer.name}
                            </div>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link href={customerHref} className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <Badge variant={customer.type === 'BUSINESS' ? 'default' : 'secondary'} className="gap-1">
                            {customer.type === 'BUSINESS' ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                            {customer.type}
                          </Badge>
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Link href={customerHref}>{customer.email}</Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Link href={customerHref}>{customer.phone}</Link>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        <Link href={customerHref}>{customer.tin_number}</Link>
                      </TableCell>
                      {showArchived && (
                        <TableCell>
                          {isArchived ? <Badge variant="destructive">Archived</Badge> : <Badge variant="outline">Active</Badge>}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm" className="h-8">
                          <Link href={customerHref} aria-label={`Open customer ${customerName}`}>
                            Open
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  )
}
